import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  TextField,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Layers as LayersIcon,
  Speed as SpeedIcon,
  Send as SendIcon,
  Edit as EditIcon
} from '@mui/icons-material';

/**
 * PlanView Component
 * Displays the high-level plan and lets user approve or cancel
 */
const PlanView = ({ plan, planId, onApprove, onCancel, onModify, loading }) => {
  const [modificationMessage, setModificationMessage] = useState('');
  const [isModifying, setIsModifying] = useState(false);
  const [showModifyInput, setShowModifyInput] = useState(false);

  // Repair plans fix the existing flow with targeted changes; greenfield plans
  // build a brand-new flow. Copy differs so the user knows nothing is rebuilt.
  const isRepair = plan.planKind === 'repair';

  const getStepIcon = (stepType) => {
    // Return appropriate icon based on step type
    return <LayersIcon fontSize="small" />;
  };

  const getActionChip = (action) => {
    const chips = {
      modify: { label: 'modify', color: 'warning' },
      insert: { label: 'add step', color: 'success' },
      remove: { label: 'remove', color: 'error' }
    };
    return chips[action] || null;
  };

  const getComplexityColor = (complexity) => {
    const colors = {
      'Simple': 'success',
      'Medium': 'warning',
      'Complex': 'error'
    };
    return colors[complexity] || 'default';
  };

  const handleSendModification = async () => {
    if (!modificationMessage.trim() || !onModify) return;
    
    setIsModifying(true);
    try {
      await onModify(modificationMessage);
      setModificationMessage('');
      setShowModifyInput(false);
    } catch (error) {
      console.error('Error modifying plan:', error);
    } finally {
      setIsModifying(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendModification();
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto', p: 2 }}>
      {/* Header */}
      <Paper elevation={3} sx={{ p: 3, mb: 3, backgroundColor: '#f8f9fa' }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#1976d2' }}>
          {isRepair ? '🩹 Repair Plan: ' : '📋 Flow Plan: '}{plan.title}
        </Typography>

        {isRepair && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Fixing your existing flow{plan.existingStepCount ? ` (${plan.existingStepCount} steps)` : ''} — only the changes below will be applied.
          </Typography>
        )}
        
        <Box sx={{ display: 'flex', gap: 2, mt: 2, alignItems: 'center' }}>
          <Chip 
            icon={<SpeedIcon />}
            label={`${plan.complexity} Complexity`}
            color={getComplexityColor(plan.complexity)}
            size="medium"
          />
          <Chip 
            label={isRepair ? `${plan.totalSteps} Change${plan.totalSteps === 1 ? '' : 's'}` : `${plan.totalSteps} Steps`}
            color="primary"
            size="medium"
          />
        </Box>
      </Paper>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          {isRepair
            ? 'This plan fixes your existing flow — it will NOT rebuild it. When you approve, only the steps listed below are changed; every other step and connection stays exactly as it is.'
            : 'This is a high-level plan. When you click "Start Building", I\'ll build each step one by one and you\'ll see them appear on the canvas in real-time. You can cancel at any point.'}
        </Typography>
      </Alert>

      {/* Diagnosis (repair plans only) */}
      {isRepair && plan.diagnosis && (
        <Paper elevation={2} sx={{ p: 3, mb: 3, backgroundColor: '#fffde7' }}>
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
            🔍 Diagnosis
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {plan.diagnosis}
          </Typography>
        </Paper>
      )}

      {/* Steps List */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          {isRepair ? 'Planned Changes' : 'Planned Steps'}
        </Typography>

        <List>
          {plan.steps.map((step, index) => (
            <React.Fragment key={step.stepNumber}>
              <ListItem 
                sx={{ 
                  py: 2,
                  '&:hover': { 
                    backgroundColor: '#f5f5f5' 
                  }
                }}
              >
                <ListItemIcon>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: '#1976d2',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600,
                      fontSize: '0.875rem'
                    }}
                  >
                    {step.stepNumber}
                  </Box>
                </ListItemIcon>
                
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {step.name}
                      </Typography>
                      {isRepair && getActionChip(step.action) ? (
                        <Chip
                          label={getActionChip(step.action).label}
                          color={getActionChip(step.action).color}
                          size="small"
                          sx={{ fontSize: '0.75rem' }}
                        />
                      ) : (
                        <Chip 
                          label={step.type}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.75rem' }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {step.details}
                    </Typography>
                  }
                />
              </ListItem>
              {index < plan.steps.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </Paper>

      {/* Plan Modification Section */}
      {showModifyInput ? (
        <Paper elevation={2} sx={{ p: 3, mb: 3, backgroundColor: '#fff3e0' }}>
          <Typography variant="body1" gutterBottom sx={{ fontWeight: 600, mb: 2, color: '#e65100' }}>
            ✏️ Modify the Plan
          </Typography>
          
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Describe what you'd like to change about the plan. The AI will update it based on your feedback.
            </Typography>
          </Alert>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="E.g., 'Add a step to validate the data before matching' or 'Remove the price list matching step'"
              value={modificationMessage}
              onChange={(e) => setModificationMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isModifying}
              variant="outlined"
              sx={{ backgroundColor: 'white' }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <IconButton
                color="primary"
                onClick={handleSendModification}
                disabled={!modificationMessage.trim() || isModifying}
                sx={{ 
                  backgroundColor: '#1976d2',
                  color: 'white',
                  '&:hover': { backgroundColor: '#1565c0' },
                  '&:disabled': { backgroundColor: '#e0e0e0', color: '#9e9e9e' }
                }}
              >
                {isModifying ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
              </IconButton>
              <IconButton
                onClick={() => {
                  setShowModifyInput(false);
                  setModificationMessage('');
                }}
                disabled={isModifying}
              >
                <CancelIcon />
              </IconButton>
            </Box>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            💡 Press Enter to send, Shift+Enter for new line
          </Typography>
        </Paper>
      ) : (
        <Paper elevation={2} sx={{ p: 2, mb: 3, textAlign: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => setShowModifyInput(true)}
            disabled={loading}
          >
            Modify This Plan
          </Button>
        </Paper>
      )}

      {/* Action Buttons */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="body1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          {isRepair ? '❓ Ready to apply these fixes?' : '❓ Ready to start building?'}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={loading ? null : <StartIcon />}
            onClick={onApprove}
            disabled={loading || showModifyInput}
            sx={{ flex: 1 }}
          >
            {loading
              ? (isRepair ? 'Applying...' : 'Starting...')
              : (isRepair ? 'Yes, Apply Fixes' : 'Yes, Start Building')}
          </Button>
          
          <Button
            variant="outlined"
            color="error"
            size="large"
            startIcon={<CancelIcon />}
            onClick={onCancel}
            disabled={loading}
            sx={{ flex: 1 }}
          >
            Cancel Plan
          </Button>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          {isRepair
            ? '💡 The fixes are applied directly onto your current flow. All untouched steps and connections are preserved.'
            : '💡 Steps will appear on the canvas one by one as they\'re built. You can click on any step to review and adjust its configuration.'}
        </Typography>
      </Paper>
    </Box>
  );
};

export default PlanView;

